import { Link, NavLink } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { useSelectedCountry } from "@/context/SelectedCountryContext";
import { Globe, Menu, X, ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export default function Header() {
  const { lang, setLang, t } = useLanguage();
  const { country, countries, setSlug, clear } = useSelectedCountry();
  const [open, setOpen] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const switcherRef = useRef(null);

  // Close switcher on outside-click
  useEffect(() => {
    if (!switcherOpen) return;
    const onClick = (e) => {
      if (switcherRef.current && !switcherRef.current.contains(e.target)) {
        setSwitcherOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [switcherOpen]);

  const navItems = [
    { to: "/principles", label: t.nav.principles, id: "principles" },
    { to: "/countries", label: t.nav.countries, id: "countries" },
    { to: "/airports", label: "Airports", id: "airports" },
    { to: "/locals", label: "Locals", id: "locals" },
    { to: "/visa", label: t.nav.visa, id: "visa" },
    { to: "/quiz", label: t.nav.quiz, id: "quiz" },
    { to: "/about", label: t.nav.about, id: "about" },
  ];

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-[#E2E8F0]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="site-logo">
          <img
            src="/ethyqs-logo.png"
            alt="ETHYQS"
            width={56}
            height={56}
            className="w-14 h-14 rounded-full object-cover ring-1 ring-black/10 shrink-0"
          />
          <span className="font-display font-bold tracking-tight text-base">
            Home
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {navItems.map((item) => (
            <NavLink
              key={item.id}
              to={item.to}
              data-testid={`nav-link-${item.id}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive ? "text-[#002FA7]" : "text-[#475569] hover:text-[#002FA7]"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          {/* Destination pill — visible whenever a country is locked */}
          {country && (
            <div className="relative" ref={switcherRef}>
              <button
                data-testid="destination-pill"
                onClick={() => setSwitcherOpen((o) => !o)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-[#002FA7]/30 bg-[#EEF2FF] text-[#002FA7] text-sm font-medium hover:bg-[#002FA7] hover:text-white transition-colors"
              >
                <span className="text-base leading-none">{country.flag}</span>
                <span className="hidden sm:inline">{country.name}</span>
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${switcherOpen ? "rotate-180" : ""}`} strokeWidth={2} />
              </button>
              {switcherOpen && (
                <div
                  data-testid="destination-switcher"
                  className="absolute right-0 mt-2 w-64 max-h-[70vh] overflow-y-auto bg-white border border-[#E2E8F0] rounded-sm shadow-lg z-50 p-1.5"
                >
                  <div className="overline px-3 py-2 text-[#94A3B8]">Switch destination</div>
                  {countries.map((c) => (
                    <button
                      key={c.slug}
                      data-testid={`switcher-country-${c.slug}`}
                      onClick={() => {
                        setSlug(c.slug);
                        setSwitcherOpen(false);
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-sm text-left text-sm transition-colors ${
                        c.slug === country.slug
                          ? "bg-[#002FA7] text-white"
                          : "text-[#0F172A] hover:bg-[#EEF2FF]"
                      }`}
                    >
                      <span className="text-base">{c.flag}</span>
                      <span className="font-medium">{c.name}</span>
                    </button>
                  ))}
                  <div className="border-t border-[#E2E8F0] mt-1 pt-1">
                    <button
                      data-testid="switcher-clear"
                      onClick={() => {
                        clear();
                        setSwitcherOpen(false);
                      }}
                      className="w-full text-left px-3 py-2 text-xs text-[#94A3B8] hover:text-[#B91C1C] transition-colors"
                    >
                      Clear destination
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "en" ? "hi" : "en")}
            className="hidden sm:inline-flex items-center gap-1.5 text-sm text-[#475569] hover:text-[#002FA7] transition-colors"
          >
            <Globe className="w-4 h-4" strokeWidth={1.5} />
            <span className="uppercase tracking-[0.15em] text-xs font-semibold">
              {lang === "en" ? "हिंदी" : "EN"}
            </span>
          </button>
          <button
            className="lg:hidden text-[#0F172A]"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-[#E2E8F0] bg-white"
        >
          <div className="px-6 py-4 flex flex-col gap-3">
            {navItems.map((item) => (
              <NavLink
                key={item.id}
                to={item.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${item.id}`}
                className={({ isActive }) =>
                  `text-sm font-medium ${
                    isActive ? "text-[#002FA7]" : "text-[#475569]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <button
              data-testid="lang-toggle-mobile"
              onClick={() => setLang(lang === "en" ? "hi" : "en")}
              className="flex items-center gap-2 text-sm text-[#475569] pt-2 border-t border-[#E2E8F0]"
            >
              <Globe className="w-4 h-4" strokeWidth={1.5} />
              <span>{lang === "en" ? "हिंदी" : "English"}</span>
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
